import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSignature, Save, RotateCcw, Loader2, CheckCircle2 } from "lucide-react";

export default function RelazioneFrameworks() {
  const [products, setProducts] = useState([]);
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      setProducts(data);
      if (data.length) setCode(data[0].code);
    });
  }, []);

  useEffect(() => {
    if (!code) return;
    setLoading(true);
    api.get(`/admin/products/${code}/framework`).then(({ data }) => {
      setData(data); setText(data.framework);
    }).finally(() => setLoading(false));
  }, [code]);

  const save = async () => {
    setBusy("save");
    try {
      await api.put(`/admin/products/${code}/framework`, { framework: text });
      setData((d) => ({ ...d, is_custom: true }));
      toast.success("Framework salvato");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const reset = async () => {
    setBusy("reset");
    try {
      const { data } = await api.post(`/admin/products/${code}/framework/reset`);
      setText(data.framework); setData((d) => ({ ...d, is_custom: false, framework: data.framework }));
      toast.success("Ripristinato il framework predefinito");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-3xl font-semibold text-forest">
          <FileSignature className="h-7 w-7 text-terracotta" /> Framework relazioni
        </h1>
        <p className="mt-1 text-muted-foreground">Modifica la traccia usata dall'AI per generare la Relazione tecnica di ciascun prodotto. Le modifiche si applicano alla prossima generazione/rigenerazione.</p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Select value={code} onValueChange={setCode}>
          <SelectTrigger data-testid="framework-product" className="sm:w-96"><SelectValue placeholder="Prodotto..." /></SelectTrigger>
          <SelectContent>{products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
        {data && (
          <span className={`flex items-center gap-1.5 text-xs ${data.is_custom ? "text-terracotta" : "text-emerald-600"}`}>
            <CheckCircle2 className="h-3.5 w-3.5" /> {data.is_custom ? "Framework personalizzato attivo" : "Framework predefinito"}
          </span>
        )}
      </div>

      <Card className="rounded-xl border-border/60 p-5">
        {loading ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>
        ) : (
          <>
            <Textarea data-testid="framework-editor" value={text} onChange={(e) => setText(e.target.value)}
              className="min-h-[52vh] font-mono text-xs leading-relaxed" spellCheck={false} />
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={save} disabled={busy === "save" || text === data?.framework} data-testid="framework-save" className="gap-1.5 bg-forest hover:bg-forest-hover">
                {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva framework
              </Button>
              <Button onClick={reset} disabled={busy === "reset" || !data?.is_custom} variant="outline" data-testid="framework-reset" className="gap-1.5">
                {busy === "reset" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Ripristina predefinito
              </Button>
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">Suggerimento: mantieni i titoli delle sezioni numerati in MAIUSCOLO. L'AI risponde solo con i dati del preventivo e le CGA, senza inventare.</p>
          </>
        )}
      </Card>
    </div>
  );
}
